/* mamagoods warmer companion — app shell, router and dock.
   Single storefront page-app with hash routing so the global timer and
   reminders keep running across views without reloads. */
import { icon } from "warmer-icons";
import {
  logoHtml,
  cn,
  esc,
  agoLabel,
  inLabel,
  pad2,
} from "warmer-util";
import { storage } from "warmer-storage";
import { timer, renderTimer } from "warmer-timer";
import { reminders, renderReminders } from "warmer-reminders";
import {
  renderGuideIndex,
  renderHomeWarmer,
  renderTravelWarmer,
} from "warmer-guide";
import { renderLog } from "warmer-log";
import { renderFaq } from "warmer-faq";
import { moduleTile, infoAccordion, sectionHeading } from "warmer-ui";
import { reminderDefs, feedTypeLabels } from "warmer-data";

let root, mainEl, dockEl;
let currentCleanup = null;

function isDark() {
  return root.classList.contains("dark");
}

function currentHash() {
  const h = location.hash;
  return h && h.startsWith("#/") ? h : "#/";
}

function navigate(hash) {
  if (location.hash === hash) route();
  else location.hash = hash;
}

/* ---- header used by sub-pages ---- */
function headerHtml({ title, back }) {
  return (
    '<header class="flex items-center gap-1 py-2">' +
    (back
      ? '<button type="button" data-nav="' + esc(back) +
        '" aria-label="Back" class="touch-target -ml-3 flex items-center justify-center rounded-full text-muted-foreground active:bg-muted">' +
        icon("chevron-left", "h-7 w-7") + "</button>"
      : "") +
    '<h1 class="text-xl font-bold">' + esc(title) + "</h1>" +
    themeToggleHtml("-mr-3 ml-auto") +
    "</header>"
  );
}

function themeToggleHtml(extra) {
  return (
    '<button type="button" data-action="theme" aria-label="Toggle theme" class="' +
    cn(
      "touch-target flex items-center justify-center rounded-full text-muted-foreground active:bg-muted",
      extra
    ) +
    '" data-theme>' +
    icon(isDark() ? "sun" : "moon", "h-6 w-6") +
    "</button>"
  );
}

function toggleTheme() {
  const next = !isDark();
  root.classList.toggle("dark", next);
  storage.setPref("theme", next ? "dark" : "light");
  document
    .querySelectorAll("[data-theme]")
    .forEach((b) => (b.innerHTML = icon(next ? "sun" : "moon", "h-6 w-6")));
}

const ctx = { headerHtml, currentHash, navigate, icon };

/* ---- home (dashboard + tiles) ---- */
async function renderHome(main) {
  const entries = await storage.getFeedLog();
  const now = Date.now();
  const last = entries[0] || null;

  const targets = reminders.get().targets;
  const set = reminderDefs
    .map((d) => ({ d, target: targets[d.id] }))
    .filter((x) => typeof x.target === "number");
  const due = set.filter((x) => x.target <= now);
  const upcoming = set
    .filter((x) => x.target > now)
    .sort((a, b) => a.target - b.target);
  const next = due[0] || upcoming[0] || null;
  const nextDue = next ? next.target <= now : false;

  const feedValue = last ? agoLabel(last.ts, now) : "–";
  const feedSub = last
    ? feedTypeLabels[last.type] + (last.volumeMl ? ", " + last.volumeMl + " mL" : "")
    : "No feeds logged yet";
  const remValue = next ? inLabel(next.target, now) : "–";
  const remSub = next ? next.d.title : "No reminder set";

  main.innerHTML =
    '<header class="flex items-center justify-between py-2">' +
    logoHtml("text-2xl") +
    themeToggleHtml("-mr-3") +
    "</header>" +
    '<p class="mt-1 text-sm text-muted-foreground">Bottle warmer set companion</p>' +
    // dashboard
    '<section class="mt-4 overflow-hidden rounded-2xl border border-border bg-card">' +
    dashRow("notebook-pen", "Last feed", feedSub, feedValue, false, "#/log") +
    dashRow("bell", "Next reminder", remSub, remValue, nextDue, "#/reminders", true) +
    "</section>" +
    // tiles
    '<div class="mt-5 grid grid-cols-2 gap-3">' +
    moduleTile("#/guide", "book-open", "Guide", "How to use both warmers") +
    moduleTile("#/timer", "timer", "Warming timer", "Time by volume and temp") +
    moduleTile("#/log", "notebook-pen", "Feeding log", "Track feeds and stats") +
    moduleTile("#/reminders", "bell", "Reminders", "Feeds, cleaning, water") +
    moduleTile("#/faq", "circle-help", "FAQ", "Quick answers") +
    moduleTile("#/support", "life-buoy", "Support", "Get help with the device") +
    "</div>";
}

function dashRow(iconName, label, sub, value, accent, href, topBorder) {
  return (
    '<button type="button" data-nav="' + esc(href) + '" class="' +
    cn(
      "flex w-full items-center justify-between gap-3 px-4 py-3 text-left active:bg-muted",
      topBorder && "border-t border-border"
    ) +
    '">' +
    '<div class="flex min-w-0 items-center gap-3">' +
    icon(iconName, "h-5 w-5 shrink-0 text-muted-foreground") +
    '<div class="min-w-0"><div class="text-sm font-medium">' + esc(label) + "</div>" +
    '<div class="truncate text-xs text-muted-foreground">' + esc(sub) + "</div></div></div>" +
    '<div class="flex items-center gap-1">' +
    '<span class="' +
    cn("text-base font-bold tabular-nums", accent && "text-accent") +
    '">' + esc(value) + "</span>" +
    icon("chevron-right", "h-4 w-4 text-muted-foreground") +
    "</div></button>"
  );
}

/* ---- support (port of app/support/page.tsx) ---- */
function renderSupport(main) {
  const email =
    (window.MG_SETTINGS && window.MG_SETTINGS.supportEmail) || "support@mamagoods.net";

  main.innerHTML =
    headerHtml({ title: "Support", back: "#/" }) +
    sectionHeading("wrench", "Try this first") +
    '<div class="mt-4 flex flex-col gap-3">' +
    infoAccordion("The warmer stopped heating", "<p>The water probably boiled dry, so dry-heat protection switched heating off. Let the unit cool a little, add at least 40 mL of water, and it will resume on its own.</p>") +
    infoAccordion("The warmer does not turn on", "<p>Check that the plug sits firmly in the outlet and the outlet works (try a lamp in it). Make sure the knob is set to a mode, not to off.</p>") +
    infoAccordion("My bottle does not fit", "<p>The chamber takes bottles up to 75 mm (2.95 in) wide. Tall bottles heat with the lid off. Wide-neck jars can go in without their caps.</p>") +
    infoAccordion("The travel sleeve is not warming", "<p>Check the USB-C cable is fully plugged in on both ends and the power source gives 5V / 2A. Some power banks switch off at low load: press their power button to wake them. Remember the sleeve keeps a warm bottle warm, it does not heat from cold.</p>") +
    "</div>" +
    '<section class="mt-9 border-t border-border pt-8">' +
    sectionHeading("mail", "Still need help? Write to us") +
    "</section>" +
    '<div class="mt-4 flex flex-col gap-3">' +
    '<label class="block"><span class="text-sm text-muted-foreground">Topic</span>' +
    '<select id="mg-sup-topic" class="touch-target mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-base">' +
    ["Home warmer", "Travel warmer", "Cleaning & care", "Something else"]
      .map((t) => '<option value="' + esc(t) + '">' + esc(t) + "</option>")
      .join("") +
    "</select></label>" +
    '<label class="block"><span class="text-sm text-muted-foreground">What is going on?</span>' +
    '<textarea id="mg-sup-msg" rows="4" placeholder="Describe the issue" class="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"></textarea></label>' +
    '<label class="block"><span class="text-sm text-muted-foreground">Your email for the reply</span>' +
    '<input id="mg-sup-email" type="email" inputmode="email" placeholder="you@example.com" class="touch-target mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent" /></label>' +
    '<button type="button" id="mg-sup-send" class="touch-target mt-1 flex items-center justify-center gap-2 rounded-2xl bg-foreground px-4 py-4 text-base font-bold text-background active:opacity-90">' +
    icon("mail", "h-5 w-5") + " Send email</button>" +
    '<p class="text-center text-xs text-muted-foreground">Opens your mail app with everything pre-filled. Attach a photo of the issue there if you can.</p>' +
    "</div>";

  main.querySelector("#mg-sup-send").addEventListener("click", () => {
    const topic = main.querySelector("#mg-sup-topic").value;
    const msg = main.querySelector("#mg-sup-msg").value;
    const from = main.querySelector("#mg-sup-email").value;
    const subject = "mamagoods support: " + topic;
    const body = msg + (from ? "\n\nReply to: " + from : "");
    window.location.href =
      "mailto:" + email + "?subject=" + encodeURIComponent(subject) +
      "&body=" + encodeURIComponent(body);
  });
}

/* ---- dock: reminder alerts + timer bar + bottom nav ---- */
function reminderAlertsHtml(hash) {
  if (hash === "#/reminders") return "";
  const s = reminders.get();
  const due = reminderDefs.filter((r) => {
    const t = s.targets[r.id];
    return t !== undefined && t <= s.now;
  });
  if (!due.length) return "";
  return due
    .map(
      (r) =>
        '<div class="flex items-center gap-2 border-t border-border bg-accent px-4 py-3 text-accent-foreground">' +
        '<button type="button" data-nav="#/reminders" class="flex flex-1 items-center gap-2">' +
        icon("bell", "h-5 w-5 shrink-0", 2) +
        '<span class="text-sm font-bold">' + esc(r.title) + ": now</span></button>" +
        '<button type="button" data-dismiss-rem="' + r.id +
        '" aria-label="Dismiss" class="touch-target -mr-2 flex items-center justify-center">' +
        icon("x", "h-5 w-5") + "</button></div>"
    )
    .join("");
}

function timerBarHtml(hash) {
  if (hash === "#/timer") return "";
  const t = timer.get();
  const active = t.status === "running" || t.status === "paused" || t.status === "done";
  if (!active) return "";
  const mm = Math.floor(t.remaining / 60);
  const ss = t.remaining % 60;
  const progress =
    t.status === "done"
      ? 1
      : t.totalSeconds > 0
        ? 1 - t.remaining / t.totalSeconds
        : 0;
  const label =
    t.status === "done"
      ? "Bottle is ready"
      : t.status === "paused"
        ? "Warming paused"
        : "Warming";
  return (
    '<button type="button" data-nav="#/timer" class="block w-full border-t border-border bg-card/95 px-4 pb-2 pt-3 text-left backdrop-blur active:bg-muted">' +
    '<div class="flex items-center justify-between">' +
    '<div class="flex items-center gap-2">' +
    icon("timer", t.status === "done" ? "h-5 w-5 text-accent" : "h-5 w-5") +
    '<span class="text-sm font-medium">' + label + "</span></div>" +
    '<span class="tabular-nums text-base font-bold">' + pad2(mm) + ":" + pad2(ss) + "</span></div>" +
    '<div class="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">' +
    '<div class="h-full rounded-full bg-accent transition-[width] duration-300 ease-linear" style="width:' +
    Math.min(progress * 100, 100) + '%"></div></div></button>'
  );
}

const navItems = [
  { href: "#/", label: "Home", icon: "home" },
  { href: "#/guide", label: "Guide", icon: "book-open" },
  { href: "#/timer", label: "Timer", icon: "timer" },
  { href: "#/log", label: "Log", icon: "notebook-pen" },
];

function bottomNavHtml(hash) {
  return (
    '<nav class="border-t border-border bg-background/95 pb-safe backdrop-blur"><div class="grid grid-cols-4">' +
    navItems
      .map((it) => {
        const active = it.href === "#/" ? hash === "#/" : hash.startsWith(it.href);
        return (
          '<button type="button" data-nav="' + it.href + '" class="' +
          cn(
            "touch-target flex flex-col items-center justify-center gap-1 py-2",
            active ? "text-accent" : "text-muted-foreground"
          ) +
          '">' + icon(it.icon, "h-6 w-6", active ? 2.25 : 1.75) +
          '<span class="text-[11px] font-medium">' + it.label + "</span></button>"
        );
      })
      .join("") +
    "</div></nav>"
  );
}

function renderDock() {
  const hash = currentHash();
  dockEl.innerHTML =
    '<div class="mx-auto max-w-md">' +
    reminderAlertsHtml(hash) +
    timerBarHtml(hash) +
    bottomNavHtml(hash) +
    "</div>";
}

/* ---- router ---- */
const routes = {
  "#/": renderHome,
  "#/guide": renderGuideIndex,
  "#/guide/home-warmer": renderHomeWarmer,
  "#/guide/travel-warmer": renderTravelWarmer,
  "#/timer": renderTimer,
  "#/log": renderLog,
  "#/reminders": renderReminders,
  "#/faq": renderFaq,
  "#/support": renderSupport,
  "#/support/": renderSupport,
};

function route() {
  if (currentCleanup) {
    try {
      currentCleanup();
    } catch {
      /* ignore */
    }
    currentCleanup = null;
  }
  const hash = currentHash();
  const view = routes[hash] || renderHome;
  const result = view(mainEl, ctx);
  if (typeof result === "function") currentCleanup = result;
  mainEl.scrollTop = 0;
  renderDock();
}

/* ---- bootstrap ---- */
async function boot() {
  root = document.getElementById("mg-app");
  if (!root) return;

  // theme (the inline script may already have set it; keep in sync)
  const pref = storage.getPref("theme", null);
  if (pref === "dark") root.classList.add("dark");
  else if (pref === "light") root.classList.remove("dark");

  root.innerHTML =
    '<div class="relative mx-auto flex h-dvh max-w-md flex-col overflow-hidden bg-background">' +
    '<main id="mg-main" class="flex-1 overflow-y-auto px-4 pb-28 pt-4"></main>' +
    '<div id="mg-dock" class="absolute inset-x-0 bottom-0"></div>' +
    "</div>";
  mainEl = root.querySelector("#mg-main");
  dockEl = root.querySelector("#mg-dock");

  // global event delegation
  root.addEventListener("click", (e) => {
    const nav = e.target.closest("[data-nav]");
    if (nav) {
      navigate(nav.getAttribute("data-nav"));
      return;
    }
    if (e.target.closest('[data-action="theme"]')) {
      toggleTheme();
      return;
    }
    const dis = e.target.closest("[data-dismiss-rem]");
    if (dis) reminders.clear(dis.getAttribute("data-dismiss-rem"));
  });

  await Promise.all([timer.init(), reminders.init()]);

  // keep the dock live as global state changes
  timer.subscribe(renderDock);
  reminders.subscribe(renderDock);

  window.addEventListener("hashchange", route);
  route();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
